import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import BG_VIDEO from '../bg-tech.mp4';
import { motion, AnimatePresence } from 'framer-motion';
import './LoginScanner.css';
import DeveloperCredit from './DeveloperCredit';
import HelportLogo from './HelportLogo';
import QrScannerModal from './QrScannerModal';
import { formatAttendanceTime } from '../utils/timeHelpers';
import { parseQrAttendancePayload, findEmployeeByUid } from '../utils/qrHelpers';
import { SCAN_DEBOUNCE_MS, SCAN_COOLDOWN_MS, createRfidScanGuard, RFID_MIN_UID_LENGTH } from '../utils/scanCooldown';
import { useLiveVideoQrScan } from '../hooks/useLiveVideoQrScan';

const LoginScanner = ({
  videoRef, allLogs, registeredEmployees, scannedUser, error,
  inputValue, setInputValue, handleAttendance, setIsAdminPortal, isAdminPortal,
  handleAdminAuth, adminCredential, setAdminCredential, adminPass, setAdminPass,
  isDarkMode, setIsDarkMode, isFullscreen = false, onBackToDashboard
}) => {

  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const qrFileInputRef = useRef(null);
  const scanDebounceRef = useRef(null);
  const rfidScanGuardRef = useRef(createRfidScanGuard(SCAN_COOLDOWN_MS));

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 10);
    return () => clearInterval(timer);
  }, []);

  const formattedTime = currentDateTime.toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
  }) + `:${String(currentDateTime.getMilliseconds()).padStart(3, '0')}`;

  const formattedDate = currentDateTime.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

  const themeStyles = {
    cardBg: isDarkMode ? 'rgba(20, 26, 32, 0.95)' : '#ffffff',
    cardText: isDarkMode ? '#ffffff' : '#1a202c',
    subText: isDarkMode ? 'rgba(255,255,255,0.7)' : '#4a5568',
    inputBg: isDarkMode ? '#11161B' : '#f7fafc',
    inputBorder: isDarkMode ? 'rgba(45, 212, 191, 0.2)' : '#e2e8f0',
    btnSecondary: isDarkMode ? 'rgba(255,255,255,0.1)' : '#edf2f7',
    btnSecondaryText: isDarkMode ? '#ffffff' : '#2d3748'
  };

  const sortedLogs = useMemo(() => {
    if (!allLogs || allLogs.length === 0) return [];
    return [...allLogs].sort((a, b) => {
      const timeA = a.real_timestamp || 0;
      const timeB = b.real_timestamp || 0;
      if (timeB === timeA) return (b.id || 0) - (a.id || 0);
      return timeB - timeA;
    }).slice(0, 20);
  }, [allLogs]);

  const buttonVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    hover: {
      scale: 1.03,
      boxShadow: isDarkMode ? "0px 0px 15px rgba(0, 204, 143, 0.4)" : "0px 4px 10px rgba(0,0,0,0.1)",
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.97 }
  };

  const cardVariants = {
    initial: { scale: 0.9, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  const normalizeUid = (value) => parseQrAttendancePayload(value);

  const tryInstantScan = useCallback((rawValue, options = {}) => {
    const { fromCamera = false } = options;
    if (scannedUser) return;

    const uid = normalizeUid(rawValue);
    if (!uid || uid.length < RFID_MIN_UID_LENGTH) return;
    if (fromCamera && !String(rawValue).trim().toUpperCase().startsWith('HELPORT:')) return;

    const guard = rfidScanGuardRef.current;
    if (!guard.canScan(uid)) return;

    const emp = findEmployeeByUid(registeredEmployees, uid);
    if (!emp) {
      if (fromCamera || String(rawValue).trim().toUpperCase().startsWith('HELPORT:')) {
        guard.lock(uid);
        handleAttendance(rawValue);
        setInputValue('');
      }
      return;
    }

    guard.lock(uid);
    handleAttendance(rawValue);
    setInputValue('');
  }, [registeredEmployees, handleAttendance, setInputValue, scannedUser]);

  const processRfidInput = useCallback((rawValue) => {
    window.clearTimeout(scanDebounceRef.current);
    scanDebounceRef.current = window.setTimeout(() => {
      tryInstantScan(rawValue);
    }, SCAN_DEBOUNCE_MS);
  }, [tryInstantScan]);

  const liveQrEnabled = !isAdminPortal && !scannedUser && !qrScannerOpen;

  useLiveVideoQrScan({
    videoRef,
    enabled: liveQrEnabled,
    onDetect: (payload) => tryInstantScan(payload, { fromCamera: true }),
  });

  const scheduleInstantScan = useCallback((rawValue) => {
    if (scannedUser) return;
    processRfidInput(rawValue);
  }, [processRfidInput, scannedUser]);

  useEffect(() => () => window.clearTimeout(scanDebounceRef.current), []);

  const handleRfidEnter = useCallback((rawValue) => {
    window.clearTimeout(scanDebounceRef.current);
    tryInstantScan(rawValue);
  }, [tryInstantScan]);

  const handleInputKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleRfidEnter(e.target.value);
  };

  const handleQrFileUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode('helport-qr-file-reader');
      const decodedText = await scanner.scanFile(file, false);
      scanner.clear();
      const uid = parseQrAttendancePayload(decodedText);
      if (!uid) {
        alert('Invalid QR code. Please use a Helport employee QR badge.');
        return;
      }
      tryInstantScan(decodedText);
    } catch (err) {
      console.error('QR file scan failed:', err);
      alert('Could not read QR code from this image. Please try again.');
    }
  }, [tryInstantScan]);

  const handleInputChange = (e) => {
    if (scannedUser) return;
    const next = e.target.value;
    setInputValue(next);
    scheduleInstantScan(next);
  };

  const handleScanWithSnapshot = (e) => {
    e.preventDefault();
    handleRfidEnter(inputValue);

    const currentUID = normalizeUid(inputValue);
    if (!currentUID) return;

    const emp = findEmployeeByUid(registeredEmployees, currentUID);
    const userName = emp ? emp.name : currentUID || 'Unknown';

    requestAnimationFrame(() => {
      if (videoRef && videoRef.current && videoRef.current.readyState >= 2) {
        try {
          const video = videoRef.current;
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          const link = document.createElement('a');
          link.href = dataUrl;

          const timeStamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          link.download = `Helport_Snapshot_${userName}_${timeStamp}.jpg`;

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (err) {
          console.error('Snapshot capture failed:', err);
        }
      }
    });
  };

  return (
    <div className={`login-wrapper ${isDarkMode ? 'dark-mode' : 'light-mode'} ${isFullscreen ? 'monitor-fullscreen' : ''}`}>

      <video autoPlay loop muted playsInline className="bg-video">
        <source src={BG_VIDEO} type="video/mp4" />
      </video>

      {isFullscreen ? (
        <div className="monitor-top-bar">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="monitor-folder-btn"
            onClick={() => alert("Automatic Saved at SNAPSHOT FOLDER")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            Folder Linked: Snapshot Folder
          </motion.button>

          {onBackToDashboard && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="monitor-back-btn"
              onClick={onBackToDashboard}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Dashboard
            </motion.button>
          )}
        </div>
      ) : (
        <motion.div
          initial={{ x: -100 }}
          animate={{ x: 0 }}
          whileHover={{ scale: 1.05 }}
          style={{
            position: 'absolute', top: '20px', left: '20px',
            backgroundColor: '#00cc8f', color: '#ffffff',
            padding: '8px 15px', fontWeight: 'bold', fontSize: '12px',
            borderRadius: '4px', zIndex: 100, cursor: 'pointer',
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)', letterSpacing: '0.5px'
          }}
          onClick={() => alert("Automatic Saved at SNAPSHOT FOLDER")}
        >
          Folder Linked: SNAPSHOT FOLDER
        </motion.div>
      )}

      <div className={isFullscreen ? 'monitor-main-fullscreen' : ''}>
        <div className="live-camera-container">
          <video ref={videoRef} autoPlay playsInline muted className="live-video-feed"></video>

          <div className="camera-hud">
            <div className="camera-timestamp">
              <span>{formattedDate}</span>
              <span className="camera-timestamp-divider">|</span>
              <span>{formattedTime}</span>
            </div>
            <div className="camera-live-badge">
              <span className="camera-live-dot" />
              Live View
            </div>
          </div>

          {isFullscreen && <DeveloperCredit variant="live" />}
        </div>

        <div className="side-monitor">
          <div className="monitor-inner" style={!isFullscreen ? { backgroundColor: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)' } : undefined}>
            <div className="monitor-header-text">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={!isFullscreen ? { color: themeStyles.cardText } : undefined}>Live Logs</span>
                {sortedLogs.length > 0 && isFullscreen && (
                  <span className="logs-count-badge">{sortedLogs.length}</span>
                )}
              </div>
              <div className="pulse-red"></div>
            </div>
            <div className="mini-logs-container">
              {sortedLogs.length > 0 ? (
                sortedLogs.map((log, index) => {
                  const empInfo = registeredEmployees.find(e => e.employeeId === log.employeeId);
                  const isActuallyIn = !log.timeOut || log.timeOut === "" || log.timeOut === "--:--";
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={log.id || index}
                      className={`mini-card ${isActuallyIn ? 'in-border' : 'out-border'}`}
                      style={!isFullscreen ? { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#ffffff' } : undefined}
                    >
                      <div className="mini-info">
                        <span className="m-name" style={!isFullscreen ? { fontWeight: 'bold', color: themeStyles.cardText } : undefined}>
                          {empInfo ? empInfo.name : "Unknown"}
                        </span>
                        <span className="m-time" style={!isFullscreen ? { color: themeStyles.subText } : undefined}>
                          {log.date} | {formatAttendanceTime(
                            isActuallyIn ? log.timeIn : (log.timeOut || log.timeIn),
                            empInfo?.shift,
                            isActuallyIn ? 'in' : 'out'
                          )}
                        </span>
                      </div>
                      <div className="m-type" style={!isFullscreen ? { color: isActuallyIn ? '#00cc8f' : '#eb0404' } : undefined}>
                        {isActuallyIn ? 'IN' : 'OUT'}
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <div className="logs-empty-state">
                  <div className="logs-empty-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                  </div>
                  <p className="logs-empty-title">Waiting for scans...</p>
                  <p className="logs-empty-sub">Attendance logs will appear here when RFID cards or QR backup codes are scanned.</p>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      <motion.div
        variants={cardVariants}
        initial="initial"
        animate="animate"
        className={`login-card ${error ? 'shake' : ''}`}
        style={{
          backgroundColor: themeStyles.cardBg,
          color: themeStyles.cardText,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          padding: '50px 40px 44px',
          borderRadius: '24px',
          width: '100%',
          maxWidth: '450px',
          zIndex: 10,
          position: 'relative',
        }}
      >
        {scannedUser ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              style={{ width: '150px', height: '150px', marginBottom: '15px', borderRadius: '15px', overflow: 'hidden', border: '4px solid #00bf86' }}
            >
              <img src={scannedUser.photo} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </motion.div>
            <h2 style={{ color: themeStyles.cardText, marginBottom: '10px' }}>{scannedUser.name}</h2>
            <div style={{ backgroundColor: scannedUser.scanAction === 'logout' ? '#eb0404' : '#00bf86', color: 'white', padding: '8px 20px', borderRadius: '20px', fontWeight: 'bold' }}>
              {scannedUser.scanAction === 'logout' ? 'Time Out Recorded' : 'Time In Recorded'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <div style={{ marginBottom: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <HelportLogo variant="form" showTagline onDark={isDarkMode} />
            </div>

            {error && <p className="msg-error" style={{ marginBottom: '15px' }}>{error}</p>}

            <AnimatePresence mode="wait">
              {!isAdminPortal ? (
                <motion.div
                  key="scanner"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '20px' }}
                >
                  <div className="status-pill" style={{
                    backgroundColor: isDarkMode ? 'rgba(0,204,143,0.1)' : '#f0fff4',
                    padding: '8px 20px',
                    borderRadius: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}>
                    <div className="status-dot dot-active" style={{ backgroundColor: '#00cc8f' }}></div>
                    <span style={{ color: isDarkMode ? '#00cc8f' : '#276749', fontWeight: 'bold', fontSize: '0.9rem' }}>Terminal Ready</span>
                  </div>

                  <form onSubmit={handleScanWithSnapshot} style={{ width: '100%' }}>
                    <input
                      type="text" autoFocus onBlur={(e) => e.target.focus()}
                      className="login-input" value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      readOnly={Boolean(scannedUser)}
                      placeholder="Scan RFID / QR UID"
                      style={{
                        backgroundColor: themeStyles.inputBg,
                        border: `2px solid ${themeStyles.inputBorder}`,
                        color: themeStyles.cardText,
                        textAlign: 'center',
                        width: '100%',
                        padding: '16px',
                        borderRadius: '12px',
                        fontSize: '1rem',
                        outline: 'none'
                      }}
                    />
                  </form>

                  <motion.button
                    variants={buttonVariants}
                    initial="initial" animate="animate" whileHover="hover" whileTap="tap"
                    type="button"
                    onClick={() => setQrScannerOpen(true)}
                    style={{
                      backgroundColor: isDarkMode ? 'rgba(0, 204, 143, 0.14)' : '#e6fffa',
                      color: isDarkMode ? '#00e5b8' : '#047857',
                      padding: '12px 24px',
                      borderRadius: '10px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      border: isDarkMode ? '1px solid rgba(0, 204, 143, 0.35)' : '1px solid #99f6e4',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Scan QR Code (Backup)
                  </motion.button>

                  <motion.button
                    variants={buttonVariants}
                    initial="initial" animate="animate" whileHover="hover" whileTap="tap"
                    type="button"
                    onClick={() => qrFileInputRef.current?.click()}
                    style={{
                      backgroundColor: themeStyles.btnSecondary,
                      color: themeStyles.btnSecondaryText,
                      padding: '12px 24px',
                      borderRadius: '10px',
                      fontWeight: 'bold',
                      fontSize: '0.85rem',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Upload Downloaded QR Image
                  </motion.button>

                  <input
                    ref={qrFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleQrFileUpload}
                  />
                  <div id="helport-qr-file-reader" style={{ display: 'none' }} aria-hidden="true" />

                  <motion.button
                    variants={buttonVariants}
                    initial="initial" animate="animate" whileHover="hover" whileTap="tap"
                    onClick={() => setIsAdminPortal(true)}
                    style={{
                      backgroundColor: themeStyles.btnSecondary,
                      color: themeStyles.btnSecondaryText,
                      padding: '12px 24px',
                      borderRadius: '10px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                      cursor: 'pointer',
                      width: 'auto',
                      minWidth: '160px'
                    }}
                  >
                    Admin Access
                  </motion.button>
                </motion.div>
              ) : (
                <motion.div
                  key="admin"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '15px' }}
                >
                  <h3 style={{ marginBottom: '10px', color: themeStyles.cardText }}>Admin Secure Access</h3>
                  <form onSubmit={handleAdminAuth} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
                    <input type="text" placeholder="Email" className="login-input" value={adminCredential} onChange={(e) => setAdminCredential(e.target.value)} style={{ backgroundColor: themeStyles.inputBg, border: `1px solid ${themeStyles.inputBorder}`, color: themeStyles.cardText, textAlign: 'center', width: '100%', padding: '14px', borderRadius: '10px' }} />
                    <input type="password" placeholder="Password" className="login-input" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} style={{ backgroundColor: themeStyles.inputBg, border: `1px solid ${themeStyles.inputBorder}`, color: themeStyles.cardText, textAlign: 'center', width: '100%', padding: '14px', borderRadius: '10px' }} />
                    <motion.button
                      variants={buttonVariants}
                      whileHover="hover" whileTap="tap"
                      type="submit"
                      className="admin-login-btn"
                      style={{ backgroundColor: '#00cc8f', color: 'white', border: 'none', borderRadius: '10px', padding: '14px', fontWeight: 'bold', width: '100%', cursor: 'pointer', marginTop: '10px' }}
                    >
                      Sign In
                    </motion.button>
                  </form>
                  <button
                    onClick={() => setIsAdminPortal(false)}
                    style={{ color: '#00cc8f', background: 'none', border: 'none', marginTop: '15px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    Back to Scanner
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      <motion.button
        whileHover={{ rotate: 90 }}
        whileTap={{ scale: 0.8 }}
        className="theme-toggle-btn"
        onClick={() => setIsDarkMode(!isDarkMode)}
        style={{
          backgroundColor: isDarkMode ? '#ffffff' : '#1a202c',
          color: isDarkMode ? '#1a202c' : '#ffffff',
          position: 'absolute',
          bottom: '30px',
          right: '30px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '1.2rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}
      >
        {isDarkMode ? '☀︎' : '⏾'}
      </motion.button>

      <QrScannerModal
        isOpen={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onScan={handleAttendance}
        videoRef={videoRef}
      />
    </div>
  );
};

export default LoginScanner;
